import { useCallback, useEffect, useMemo, useState } from "react";
import { OrderList, OrderSearch } from "components";
import useSession from "hooks/useSession";
import { OrderStatus, UserRole } from "types/declarations/enums.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { useLazyFilterQuery } from "features/api/orderApi.ts";
import { useDispatch } from "react-redux";
import { getFilteredOrders, objectToDateRange } from "../utils/orderListUtil";
import { CacheKeys, InitialFilterOptions, InitialOrderResponse, InitialSearchDates } from "./orderListConstants";
import { cartActions } from "features/reducers/replacementOrder/cart.ts";
import DefaultMessage from "components/Common/DefaultMessage/DefaultMessage";
import { useLocation, useNavigate } from "react-router";
import { defaultMessageForCareUserText, searchBarHelperText } from "constants/text";
import { GetOrderSummaryResponseData } from "types/api/orders/getOrderSummary.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { OrderListPageWrapper } from "pages/OrderPages/OrderListPage/OrderListPage.styles.ts";

const OrderListPage = () => {
	const { data: userDetails } = useGetUserDetailsQuery();
	const location = useLocation();
	const navigate = useNavigate();
	const [orderStatus, setOrderStatus] = useState<number>(200);
	const [currentSearch, setCurrentSearch] = useSession<string>(CacheKeys.Search, "");
	const [searchDates, setSearchDates] = useSession<DateRange>(CacheKeys.Dates, InitialSearchDates, objectToDateRange);
	const [filterOptions, setFilterOptions] = useSession<FilterOpts>(CacheKeys.Filters, InitialFilterOptions);
	const [{ items, isPartialDataset = false } = {}, setOrderResponse] = useSession<GetOrderSummaryResponseData>(
		CacheKeys.Orders,
		InitialOrderResponse
	);
	const [searchLoading, setSearchLoading] = useState(false);
	const [isInitialMount, setIsInitialMount] = useState(true);

	const dispatch = useDispatch();

	const [trigger, { isLoading, isUninitialized }] = useLazyFilterQuery();

	const isCareRole = userDetails?.role === UserRole.CARE;

	const invokeSearch = useCallback(
		async (searchQuery?: string) => {
			try {
				setSearchLoading(true);

				await Promise.resolve();
				const response = await trigger({
					searchDates,
					searchTerm: searchQuery ?? currentSearch
				}).unwrap();

				setOrderResponse(response);
				setSearchLoading(false);
			} catch (err) {
				setSearchLoading(false);
				setOrderStatus((err as any)?.status);
			}
		},
		[currentSearch, searchDates, setOrderResponse, trigger]
	);

	useEffect(() => {
		setIsInitialMount(false);
	}, []);

	useEffect(() => {
		if (!isInitialMount) {
			void invokeSearch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const params = new URLSearchParams(location.search);
	const searchValue = params.get("search");
	useEffect(() => {
		if (searchValue) {
			setCurrentSearch(searchValue);
			setFilterOptions(() => ({
				accounts: [],
				designers: [],
				brands: [],
				status: [],
				types: [],
				hasStatusTriggered: false,
				isStockOrders: false
			}));
			void invokeSearch(searchValue);
			params.delete("search");
			navigate(
				{
					search: params.toString()
				},
				{ replace: true }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const filteredOrders = useMemo(() => getFilteredOrders(filterOptions, items), [items, filterOptions]);

	const showOrders = useMemo(() => {
		if (items && items.length > 0) return true;
		return !isUninitialized || !isCareRole;
	}, [isUninitialized, isCareRole, items]);

	const hasErrors = useMemo(() => {
		return (
			items?.some((order) => {
				return order.status === OrderStatus.SUBMITTED_WITH_ERRORS || order.status === OrderStatus.HOLD;
			}) ?? false
		);
	}, [items]);

	useEffect(() => {
		dispatch(cartActions.resetCart());
		dispatch(newOrderActions.clearNewOrder());
	}, [dispatch]);

	return (
		<>
			<div data-testid="order-list-page-order-search">
				<OrderSearch
					userDetails={userDetails}
					currentSearch={currentSearch}
					setCurrentSearch={setCurrentSearch}
					invokeSearch={invokeSearch}
					hasSearchQueryParam={searchValue !== null && searchValue !== undefined}
					isLoading={isLoading || searchLoading}
					isPartialDataset={isPartialDataset && filteredOrders.length >= 1000}
					searchDates={searchDates}
					setSearchDates={setSearchDates}
				/>
			</div>
			<OrderListPageWrapper>
				{showOrders && (
					<OrderList
						isLoading={isLoading || searchLoading}
						orderStatus={orderStatus}
						filteredOrders={filteredOrders}
						orders={items}
						searchFilters={filterOptions}
						searchDates={searchDates}
						totalStatuses={items?.map((obj) => obj.status)}
						currentSearch={currentSearch}
						userDetails={userDetails}
						filterOptions={filterOptions}
						setFilterOptions={setFilterOptions}
						showOrders={showOrders}
						hasErrors={hasErrors}
					/>
				)}
				{!showOrders && (
					<DefaultMessage
						dataTestId="default-message-for-care-user"
						asset="/assets/CareSearchImage.svg"
						altMessage="Default message for care user"
						title={defaultMessageForCareUserText}
						subtitle={searchBarHelperText}
					/>
				)}
			</OrderListPageWrapper>
		</>
	);
};

export default OrderListPage;

export interface DateRange {
	fromDate: Date | undefined;
	toDate: Date | undefined;
}

export interface FilterOpts {
	assignedToLocation?: string[];
	assignedToPerson?: string[];
	accounts?: string[];
	designers?: string[];
	brands?: string[];
	status?: string[];
	types?: string[];
	hasStatusTriggered?: boolean;
	sortedBy?: string[];
	quotedBy?: string[];
	productLines?: string[];
	isStockOrders?: boolean;
}
